import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormLabel,
  Input,
  Option,
  Select,
  Stack,
  Textarea,
  Typography,
} from "@mui/joy";
import {
  ArrowDown,
  ArrowUp,
  CheckCircle,
  Copy,
  Eye,
  Plus,
  Trash,
} from "@phosphor-icons/react";
import {
  assessmentApi as api,
  errorText,
  type AssessmentCampaign,
  type AssessmentDefinition,
  type AssessmentQuestion,
  type AssessmentSettings,
  type AssessmentTemplate,
  type Answer,
} from "./assessmentApi";
import { AssessmentQuestionField } from "./AssessmentQuestionField";

const uid = () => crypto.randomUUID();
const newQuestion = (): AssessmentQuestion => ({
  id: uid(),
  prompt: "New question",
  type: "text",
  required: true,
  help: "",
  competency: "",
  max_points: 0,
  options: [],
  rubric: [],
});
const field = (
  label: string,
  value: string | number,
  set: (v: string) => void,
  type = "text",
) => (
  <FormControl>
    <FormLabel>{label}</FormLabel>
    <Input type={type} value={value} onChange={(e) => set(e.target.value)} />
  </FormControl>
);

function QuestionEditor({
  q,
  onChange,
}: {
  q: AssessmentQuestion;
  onChange: (q: AssessmentQuestion) => void;
}) {
  const patch = (v: Partial<AssessmentQuestion>) => onChange({ ...q, ...v });
  return (
    <Stack spacing={2}>
      <FormControl>
        <FormLabel>Question</FormLabel>
        <Textarea
          minRows={2}
          value={q.prompt}
          onChange={(e) => patch({ prompt: e.target.value })}
        />
      </FormControl>
      {field("Help shown to the candidate", q.help, (help) => patch({ help }))}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        <FormControl sx={{ flex: 1 }}>
          <FormLabel>Answer format</FormLabel>
          <Select
            value={q.type}
            onChange={(_, type) =>
              type &&
              patch({
                type,
                options: ["choice", "multi"].includes(type)
                  ? q.options.length
                    ? q.options
                    : [
                        {
                          id: "0",
                          label: "First option",
                          points: 0,
                          flag: false,
                        },
                        {
                          id: "1",
                          label: "Second option",
                          points: 0,
                          flag: false,
                        },
                      ]
                  : [],
                max_points: 0,
                rubric: [],
              })
            }
          >
            <Option value="text">Written response</Option>
            <Option value="choice">Choose one</Option>
            <Option value="multi">Choose several (informational)</Option>
            <Option value="number">Number</Option>
          </Select>
        </FormControl>
        {field("Skill / competency", q.competency, (competency) =>
          patch({ competency }),
        )}
        {field(
          "Maximum points (0 = informational)",
          q.max_points,
          (n) => patch({ max_points: Number(n) }),
          "number",
        )}
      </Stack>
      <Checkbox
        checked={q.required}
        onChange={(e) => patch({ required: e.target.checked })}
        label="Answer required"
      />
      <Checkbox
        checked={!!q.condition}
        onChange={(e) =>
          patch({
            condition: e.target.checked
              ? { setting: "driving_required", equals: true }
              : undefined,
          })
        }
        label="Ask only when the campaign requires driving (informational questions)"
      />
      {["choice", "multi"].includes(q.type) && (
        <Stack spacing={1}>
          {q.options.map((o, i) => (
            <Card key={o.id} variant="soft">
              <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
                <Box sx={{ flex: 1 }}>
                  {field(`Choice ${i + 1}`, o.label, (label) =>
                    patch({
                      options: q.options.map((v, n) =>
                        n === i ? { ...v, label } : v,
                      ),
                    }),
                  )}
                </Box>
                {q.max_points > 0 &&
                  field(
                    "Points",
                    o.points,
                    (n) =>
                      patch({
                        options: q.options.map((v, j) =>
                          j === i ? { ...v, points: Number(n) } : v,
                        ),
                      }),
                    "number",
                  )}
                <Checkbox
                  label="Flag for discussion"
                  checked={o.flag}
                  onChange={(e) =>
                    patch({
                      options: q.options.map((v, n) =>
                        n === i ? { ...v, flag: e.target.checked } : v,
                      ),
                    })
                  }
                />
                <Button
                  variant="plain"
                  color="danger"
                  aria-label={`Remove choice ${i + 1}`}
                  onClick={() =>
                    patch({ options: q.options.filter((_, n) => n !== i) })
                  }
                >
                  <Trash />
                </Button>
              </Stack>
            </Card>
          ))}
          <Button
            variant="outlined"
            startDecorator={<Plus />}
            onClick={() =>
              patch({
                options: [
                  ...q.options,
                  { id: uid(), label: "", points: 0, flag: false },
                ],
              })
            }
          >
            Add choice
          </Button>
        </Stack>
      )}
      {q.type === "number" && q.max_points > 0 && (
        <Stack direction="row" spacing={2}>
          {field(
            "Correct answer",
            q.answer ?? 0,
            (n) => patch({ answer: Number(n) }),
            "number",
          )}
          {field(
            "Allowed rounding difference",
            q.tolerance ?? 0.01,
            (n) => patch({ tolerance: Number(n) }),
            "number",
          )}
        </Stack>
      )}
      {q.type === "text" && q.max_points > 0 && (
        <FormControl>
          <FormLabel>Review criteria — one per line</FormLabel>
          <Textarea
            minRows={4}
            value={q.rubric.join("\n")}
            onChange={(e) => patch({ rubric: e.target.value.split("\n") })}
          />
          <Typography level="body-sm">
            Each demonstrated criterion earns an equal share of the question’s
            points. Recruiters confirm the rating.
          </Typography>
        </FormControl>
      )}
    </Stack>
  );
}

function TemplateEditor({
  initial,
  onClose,
  onSaved,
}: {
  initial: AssessmentTemplate;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [row, setRow] = useState(initial);
  const [definition, setDefinition] = useState<AssessmentDefinition>(
    structuredClone(initial.draft),
  );
  const [preview, setPreview] = useState(false);
  const [answers, setAnswers] = useState<Record<string, Answer>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const dirty = JSON.stringify(definition) !== JSON.stringify(row.draft);
  async function save(publish = false, archive = false) {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      let saved = await api.put<AssessmentTemplate>(
        `assessment-templates/${row.id}/`,
        {
          draft: {
            ...definition,
            interview: definition.interview.filter((v) => v.trim()),
            sections: definition.sections.map((s) => ({
              ...s,
              questions: s.questions.map((q) => ({
                ...q,
                rubric: q.rubric.filter((v) => v.trim()),
              })),
            })),
          },
          updated_at: row.updated_at,
          archived: archive,
        },
      );
      if (publish)
        saved = await api.post<AssessmentTemplate>(
          `assessment-templates/${row.id}/`,
          { updated_at: saved.updated_at },
        );
      setRow(saved);
      setDefinition(saved.draft);
      setNotice(
        publish
          ? "Published. Choose this version in campaign setup when you are ready."
          : "Saved. Existing assessments keep their original version.",
      );
      onSaved();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }
  const patchSection = (
    index: number,
    patch: Partial<AssessmentDefinition["sections"][number]>,
  ) =>
    setDefinition({
      ...definition,
      sections: definition.sections.map((s, i) =>
        i === index ? { ...s, ...patch } : s,
      ),
    });
  return (
    <Stack spacing={2}>
      <Stack
        direction="row"
        sx={{ justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}
      >
        <Button
          variant="plain"
          onClick={() => {
            if (!dirty || window.confirm("Discard unsaved template changes?"))
              onClose();
          }}
        >
          ← Back to library
        </Button>
        <Button
          startDecorator={<Eye />}
          variant="outlined"
          onClick={() => setPreview(!preview)}
        >
          {preview ? "Edit template" : "Try candidate preview"}
        </Button>
      </Stack>
      <Typography level="h3">
        {preview ? "Candidate preview" : "Edit assessment template"}
      </Typography>
      <Typography level="body-sm">
        Changes create a new version when published. Past answers and scores
        remain attached to the version candidates actually took.
      </Typography>
      {error && <Alert color="danger">{error}</Alert>}
      {notice && <Alert color="success">{notice}</Alert>}
      {preview ? (
        <>
          <Alert>
            Practice only — these answers are not saved as an application or
            scored.
          </Alert>
          <Typography level="h4">{definition.title}</Typography>
          <Typography>{definition.welcome}</Typography>
          {definition.sections.map((s) => (
            <Card key={s.id}>
              <Typography level="title-lg">{s.title}</Typography>
              {s.questions.map((q) => (
                <AssessmentQuestionField
                  key={q.id}
                  question={q}
                  value={answers[q.id]}
                  onChange={(v) => setAnswers({ ...answers, [q.id]: v })}
                />
              ))}
            </Card>
          ))}
        </>
      ) : (
        <>
          <Card>
            {field("Template name", definition.title, (title) =>
              setDefinition({ ...definition, title }),
            )}
            <FormControl>
              <FormLabel>Welcome message</FormLabel>
              <Textarea
                minRows={3}
                value={definition.welcome}
                onChange={(e) =>
                  setDefinition({ ...definition, welcome: e.target.value })
                }
              />
            </FormControl>
            {field(
              "Estimated time (minutes)",
              definition.estimated_minutes,
              (n) =>
                setDefinition({ ...definition, estimated_minutes: Number(n) }),
              "number",
            )}
          </Card>
          {definition.sections.map((section, si) => (
            <Card key={section.id}>
              <Stack direction="row" spacing={1}>
                {[-1, 1].map((offset) => (
                  <Button
                    key={offset}
                    variant="plain"
                    disabled={
                      si + offset < 0 ||
                      si + offset >= definition.sections.length
                    }
                    aria-label={
                      offset < 0 ? "Move section up" : "Move section down"
                    }
                    onClick={() => {
                      const sections = [...definition.sections];
                      [sections[si], sections[si + offset]] = [
                        sections[si + offset],
                        sections[si],
                      ];
                      setDefinition({ ...definition, sections });
                    }}
                  >
                    {offset < 0 ? <ArrowUp /> : <ArrowDown />}
                  </Button>
                ))}
              </Stack>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <Box sx={{ flex: 1 }}>
                  {field("Section name", section.title, (title) =>
                    patchSection(si, { title }),
                  )}
                </Box>
                {field(
                  "Score weight (%)",
                  section.weight,
                  (n) => patchSection(si, { weight: Number(n) }),
                  "number",
                )}
                <Button
                  variant="plain"
                  color="danger"
                  onClick={() =>
                    window.confirm(
                      "Remove this section and its questions from the draft?",
                    ) &&
                    setDefinition({
                      ...definition,
                      sections: definition.sections.filter((_, i) => i !== si),
                    })
                  }
                >
                  Remove section
                </Button>
              </Stack>
              <Typography level="body-sm">
                {section.questions.length} questions ·{" "}
                {section.weight === 0
                  ? "Informational — does not affect score"
                  : `${section.weight}% of overall score`}
              </Typography>
              {section.questions.map((q, qi) => (
                <Box
                  component="details"
                  key={q.id}
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: "md",
                    p: 1.5,
                  }}
                >
                  <Box component="summary" sx={{ cursor: "pointer", py: 1 }}>
                    {qi + 1}. {q.prompt}{" "}
                    <Chip size="sm" variant="soft">
                      {q.max_points
                        ? `${q.max_points} points`
                        : "Informational"}
                    </Chip>
                  </Box>
                  <Stack spacing={2} sx={{ pt: 2 }}>
                    <QuestionEditor
                      q={q}
                      onChange={(next) =>
                        patchSection(si, {
                          questions: section.questions.map((v, i) =>
                            i === qi ? next : v,
                          ),
                        })
                      }
                    />
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ flexWrap: "wrap" }}
                    >
                      {[-1, 1].map((offset) => (
                        <Button
                          key={offset}
                          variant="outlined"
                          disabled={
                            qi + offset < 0 ||
                            qi + offset >= section.questions.length
                          }
                          aria-label={
                            offset < 0
                              ? "Move question up"
                              : "Move question down"
                          }
                          onClick={() => {
                            const qs = [...section.questions];
                            [qs[qi], qs[qi + offset]] = [
                              qs[qi + offset],
                              qs[qi],
                            ];
                            patchSection(si, { questions: qs });
                          }}
                        >
                          {offset < 0 ? <ArrowUp /> : <ArrowDown />}
                        </Button>
                      ))}
                      <Button
                        variant="plain"
                        color="danger"
                        onClick={() =>
                          patchSection(si, {
                            questions: section.questions.filter(
                              (_, i) => i !== qi,
                            ),
                          })
                        }
                      >
                        Remove question
                      </Button>
                    </Stack>
                  </Stack>
                </Box>
              ))}
              <Button
                variant="outlined"
                startDecorator={<Plus />}
                onClick={() =>
                  patchSection(si, {
                    questions: [...section.questions, newQuestion()],
                  })
                }
              >
                Add question
              </Button>
            </Card>
          ))}
          <Button
            variant="outlined"
            startDecorator={<Plus />}
            onClick={() =>
              setDefinition({
                ...definition,
                sections: [
                  ...definition.sections,
                  {
                    id: uid(),
                    title: "New section",
                    weight: 0,
                    questions: [newQuestion()],
                  },
                ],
              })
            }
          >
            Add section
          </Button>
          <Card>
            <FormControl>
              <FormLabel>Interview guide — one prompt per line</FormLabel>
              <Textarea
                minRows={6}
                value={definition.interview.join("\n")}
                onChange={(e) =>
                  setDefinition({
                    ...definition,
                    interview: e.target.value.split("\n"),
                  })
                }
              />
            </FormControl>
          </Card>
          <Card sx={{ position: "sticky", bottom: 12, zIndex: 2 }}>
            <Stack
              direction="row"
              sx={{ gap: 1, flexWrap: "wrap", alignItems: "center" }}
            >
              <Typography sx={{ flex: 1 }}>
                Section weights:{" "}
                {definition.sections.reduce((n, s) => n + s.weight, 0)}% / 100%
              </Typography>
              <Button variant="outlined" loading={busy} onClick={() => save()}>
                Save draft
              </Button>
              <Button
                loading={busy}
                startDecorator={<CheckCircle />}
                onClick={() => save(true)}
              >
                Publish version
              </Button>
              <Button
                color="neutral"
                variant="plain"
                disabled={busy}
                onClick={() => save(false, !row.archived)}
              >
                {row.archived ? "Restore template" : "Archive template"}
              </Button>
            </Stack>
          </Card>
        </>
      )}
    </Stack>
  );
}

export function AssessmentLibrary({ campaignId }: { campaignId: string }) {
  const qc = useQueryClient();
  const library = useQuery({
    queryKey: ["assessment-library"],
    queryFn: () =>
      api.get<{ can_edit: boolean; templates: AssessmentTemplate[] }>(
        "assessment-templates/",
      ),
  });
  const config = useQuery({
    queryKey: ["assessment-config", campaignId],
    queryFn: () =>
      api.get<AssessmentCampaign>(`campaigns/${campaignId}/assessment/`),
  });
  const [editing, setEditing] = useState<AssessmentTemplate | null>(null);
  const [version, setVersion] = useState<number | null>(null);
  const [settings, setSettings] = useState<AssessmentSettings>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  useEffect(() => {
    if (config.data) {
      setVersion(config.data.version_id);
      setSettings(config.data.settings);
    }
  }, [config.data]);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["assessment-library"] });
  };
  async function create(starter: string, duplicate?: number) {
    setBusy(true);
    setError("");
    try {
      setEditing(
        await api.post<AssessmentTemplate>("assessment-templates/", {
          starter,
          duplicate,
        }),
      );
      refresh();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }
  async function save() {
    setBusy(true);
    setError("");
    try {
      await api.put(`campaigns/${campaignId}/assessment/`, {
        version_id: version,
        settings,
        updated_at: config.data?.updated_at,
      });
      await qc.invalidateQueries({
        queryKey: ["assessment-config", campaignId],
      });
      setNotice(
        "Campaign assessment saved. New applicants will use this selection. Existing sessions are unchanged.",
      );
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }
  if (editing)
    return (
      <TemplateEditor
        key={editing.id}
        initial={editing}
        onClose={() => setEditing(null)}
        onSaved={refresh}
      />
    );
  if (library.isLoading || config.isLoading)
    return <Typography>Loading assessment setup…</Typography>;
  if (library.error || config.error)
    return (
      <Alert color="danger">
        Unable to load assessment setup.{" "}
        <Button
          onClick={() => {
            void library.refetch();
            void config.refetch();
          }}
        >
          Retry
        </Button>
      </Alert>
    );
  return (
    <Stack spacing={3}>
      <Box>
        <Typography level="h3">Assessment setup</Typography>
        <Typography>
          Use practical job questions to support a consistent hiring
          conversation. A person makes the hiring decision.
        </Typography>
      </Box>
      {error && <Alert color="danger">{error}</Alert>}
      {notice && <Alert color="success">{notice}</Alert>}
      {!library.data?.can_edit && (
        <Alert>
          Organization admins manage templates and campaign setup. Recruiters
          can review candidate answers and scores.
        </Alert>
      )}
      <Card>
        <Typography level="title-lg">
          1. Choose the assessment for this campaign
        </Typography>
        <FormControl>
          <FormLabel>Published template version</FormLabel>
          <Select
            disabled={!config.data?.can_edit}
            value={version ?? 0}
            onChange={(_, v) => setVersion(v || null)}
          >
            <Option value={0}>No assessment — standard application</Option>
            {library.data?.templates
              .filter(
                (t) => !t.archived || t.versions.some((v) => v.id === version),
              )
              .flatMap((t) =>
                t.versions.map((v) => (
                  <Option key={v.id} value={v.id}>
                    {t.name} · Version {v.number}
                    {t.archived ? " · Archived" : ""}
                  </Option>
                )),
              )}
          </Select>
        </FormControl>
        <Typography level="body-sm">
          Publish a template below to make it available here. Candidates already
          in progress keep their original questions and scoring.
        </Typography>
        <FormControl>
          <FormLabel>
            Role details: territory, schedule, compensation and payment timing
          </FormLabel>
          <Textarea
            disabled={!config.data?.can_edit}
            minRows={4}
            value={settings.role_details ?? ""}
            onChange={(e) =>
              setSettings({ ...settings, role_details: e.target.value })
            }
          />
        </FormControl>
        {field(
          "Hiring team contact / help instructions",
          settings.help_contact ?? "",
          (help_contact) => setSettings({ ...settings, help_contact }),
        )}
        <Checkbox
          disabled={!config.data?.can_edit}
          label="Driving is an essential duty of this position"
          checked={!!settings.driving_required}
          onChange={(e) =>
            setSettings({ ...settings, driving_required: e.target.checked })
          }
        />
        <Typography level="body-sm">
          Shows the license question when included in the template. No score is
          assigned to this answer.
        </Typography>
        {config.data?.can_edit && (
          <Button loading={busy} onClick={save}>
            Save campaign assessment
          </Button>
        )}
      </Card>
      <Box>
        <Typography level="title-lg">
          2. Your organization’s template library
        </Typography>
        <Typography level="body-sm">
          Every question, answer choice, review criterion, and section weight is
          editable. Start with an original role template, then adapt it to the
          actual job.
        </Typography>
      </Box>
      {library.data?.can_edit && (
        <Stack direction="row" sx={{ flexWrap: "wrap", gap: 1 }}>
          <Button
            startDecorator={<Plus />}
            variant="outlined"
            loading={busy}
            onClick={() => create("sales")}
          >
            Roofing sales starter
          </Button>
          <Button
            startDecorator={<Plus />}
            variant="outlined"
            loading={busy}
            onClick={() => create("canvasser")}
          >
            Canvasser starter
          </Button>
        </Stack>
      )}
      {!library.data?.templates.length && (
        <Alert>
          No templates yet. An organization admin can create a starter above.
        </Alert>
      )}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
          gap: 2,
        }}
      >
        {library.data?.templates.map((t) => (
          <Card key={t.id}>
            <Typography level="title-lg">{t.name}</Typography>
            <Typography>
              {t.draft.sections.reduce((n, s) => n + s.questions.length, 0)}{" "}
              questions · About {t.draft.estimated_minutes} minutes
            </Typography>
            <Chip size="sm" variant="soft">
              {t.archived
                ? "Archived"
                : t.versions.length
                  ? `${t.versions.length} published version(s)`
                  : "Draft — not available to applicants"}
            </Chip>
            <Divider />
            {library.data.can_edit && (
              <Stack direction="row" spacing={1}>
                <Button variant="outlined" onClick={() => setEditing(t)}>
                  Edit template
                </Button>
                <Button
                  variant="plain"
                  startDecorator={<Copy />}
                  onClick={() => create("sales", t.id)}
                >
                  Duplicate
                </Button>
              </Stack>
            )}
          </Card>
        ))}
      </Box>
    </Stack>
  );
}
